import { useEffect, useCallback, useReducer, useMemo } from 'react'

import { useAppSelector, useAppDispatch } from 'global/redux/toolkit/hooks'
import { config } from 'global/lib/config'
import { logout } from 'global/redux/features/auth/authSlice'
import * as analyticsLib from 'global/lib/analytics/analyticsService'
import useUserDataLib from 'global/lib/userData/useUserData'

export interface UseSigninConnectLogicProps {
  appName: any
  location: any
  routesConfig: any
  o365Connect: any
}

export interface SigninConnectLogicProps {
  accountName: string
  appName: string
  isPermissionDialogVisible: boolean
  loggedInUser?: string
  onConnect: () => void
  onTogglePermissionDialog: () => void
  onUseAnotherAccount: () => void
  requestDemoPath: string
  sendDemoRequestTrackingEvent: () => void
}

export default function useSigninConnectLogic({
  appName,
  location,
  o365Connect,
  routesConfig
}: UseSigninConnectLogicProps): [SigninConnectLogicProps] {
  const { accounts, bccAccount, user, NEEDS_TO_CONNECT_O365_STATES } = useAppSelector((_stores: any) => ({
    accounts: _stores.user.data.accounts,
    bccAccount: _stores.accessToken.bccAccount || '',
    user: _stores.user.data,
    NEEDS_TO_CONNECT_O365_STATES: _stores.auth.NEEDS_TO_CONNECT_O365_STATES
  }))
  const [state, setState] = useReducer((_state: any, newState: any) => ({ ..._state, ...newState }), {
    isPermissionDialogVisible: false
  })
  const dispatch = useAppDispatch()
  const [userDataLib] = useUserDataLib()

  // init
  useEffect(() => {
    analyticsLib.trackAppEvent(analyticsLib.EVENTS.SIGNIN_CONNECT_PAGEVIEW, {
      url: window.location.href,
      email: user.email
    })
    if (
      !user.id ||
      (user.id &&
        !NEEDS_TO_CONNECT_O365_STATES.includes(user.state) &&
        user.state !== config.USER_STATES.PROFILE_COMPLETED)
    ) {
      routesConfig.SIGNIN_SIGNUP.goto()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onTogglePermissionDialog = useCallback(() => {
    setState({ isPermissionDialogVisible: !state.isPermissionDialogVisible })
    // we only want to send the tracking once, when the user clicks
    if (!state.isPermissionDialogVisible) {
      analyticsLib.trackAppEvent(analyticsLib.EVENTS.SIGNUP_PERMISSION, { url: window.location.href })
    }
  }, [state.isPermissionDialogVisible, setState])

  const account = useMemo(() => {
    return userDataLib.getAccountByBccId(bccAccount) || accounts?.[0]
  }, [accounts, bccAccount, userDataLib])

  const onConnect = useCallback(() => {
    analyticsLib.trackAppEvent(analyticsLib.EVENTS.SIGNIN_CONNECT_O365, {
      url: window.location.href,
      accountId: account?.accountId,
      email: user.email
    })
    o365Connect(location, true, account?.accountId)
  }, [account?.accountId, location, o365Connect, user.email])

  const onUseAnotherAccount = useCallback(() => {
    dispatch(logout())
  }, [dispatch])

  const sendDemoRequestTrackingEvent = useCallback(() => {
    analyticsLib.trackAppEvent(analyticsLib.EVENTS.SIGNUP_REQUEST_DEMO, { url: window.location.href })
  }, [])

  return useMemo(() => {
    return [
      {
        accountName: account?.accountName || '',
        appName,
        isPermissionDialogVisible: state.isPermissionDialogVisible,
        loggedInUser: user.email,
        onConnect,
        onTogglePermissionDialog,
        onUseAnotherAccount,
        requestDemoPath: config.LINKS.REQUEST_A_DEMO,
        sendDemoRequestTrackingEvent
      }
    ]
  }, [
    account?.accountName,
    appName,
    onConnect,
    onTogglePermissionDialog,
    state.isPermissionDialogVisible,
    user.email,
    onUseAnotherAccount,
    sendDemoRequestTrackingEvent
  ])
}
